"use client";

import { c as _c } from "react/compiler-runtime";
import { useInChatRoom } from "@/components/live/WorkflowPresenceProvider";
import { generateColorFromAddress } from "@/config/constants";
import { usePresenceListener } from "@ably/chat/react";
import { useAuth } from "@b3dotfun/sdk/global-account/react";
import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
export interface NodeSelectionPresence {
  /** Node ID that is selected */
  nodeId: string;
  /** Client ID of the user who selected it */
  clientId: string;
  /** Display name of the user */
  name: string;
  /** Color assigned to this user */
  color: string;
  /** B3 Global ID for avatar lookup */
  b3GlobalId?: string;
}

/**
 * Creates a stable fingerprint of selection data to detect actual changes.
 * Only includes selectedNodeId and clientId - ignores cursor positions.
 */
function createSelectionFingerprint(presenceData: Array<{
  clientId: string;
  data?: {
    selectedNodeId?: string;
    name?: string;
  };
}>, myB3GlobalId: string | undefined): string {
  const entries: string[] = [];
  for (const member of presenceData) {
    const selectedNodeId = member.data?.selectedNodeId;
    if (!selectedNodeId) continue;
    if (myB3GlobalId && member.clientId === myB3GlobalId) continue;

    // Include clientId, selectedNodeId, and name in fingerprint
    entries.push(`${member.clientId}:${selectedNodeId}:${member.data?.name || ""}`);
  }

  // Sort for consistent ordering
  return entries.sort().join("|");
}

// ============================================================================
// CONTEXT-BASED APPROACH (PERFORMANCE)
//
// Previously, each NodeSelectionIndicator (one per node, 20+ instances) independently
// called usePresenceListener(), creating 20+ duplicate subscriptions. Every presence
// update (cursor movement, selection change) triggered re-renders in ALL instances.
//
// Now: ONE provider subscribes to presence data and shares the result via context.
// Individual NodeSelectionIndicator components only re-render when the Map changes
// (which only happens when actual selections change, not cursor movements).
// ============================================================================

const EMPTY_MAP = new Map<string, NodeSelectionPresence[]>();
const EMPTY_ARRAY: NodeSelectionPresence[] = [];
const NodeSelectionPresenceContext = createContext<Map<string, NodeSelectionPresence[]>>(EMPTY_MAP);

/**
 * Provider that subscribes to presence data ONCE and shares node selection state.
 * All NodeSelectionIndicator components read from this context instead of
 * each creating their own subscription.
 */
export function NodeSelectionPresenceProvider(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  const inChatRoom = useInChatRoom();
  if (!inChatRoom) {
    let t1;
    if ($[0] !== children) {
      t1 = <NodeSelectionPresenceContext.Provider value={EMPTY_MAP}>{children}</NodeSelectionPresenceContext.Provider>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <NodeSelectionPresenceActive>{children}</NodeSelectionPresenceActive>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/** Active provider that subscribes to presence data (rendered inside ChatRoomProvider). */
function NodeSelectionPresenceActive(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const nodeSelections = useNodeSelectionPresence();
  let t1;
  if ($[0] !== children || $[1] !== nodeSelections) {
    t1 = <NodeSelectionPresenceContext.Provider value={nodeSelections}>{children}</NodeSelectionPresenceContext.Provider>;
    $[0] = children;
    $[1] = nodeSelections;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Hook to get node selection presence from other collaborators.
 * Returns a map of nodeId -> list of users who have that node selected.
 *
 * PERFORMANCE: Uses fingerprinting to avoid re-renders when only cursor positions
 * change (not node selections). This prevents N nodes from re-rendering on every
 * cursor movement.
 *
 * NOTE: This hook calls usePresenceListener directly. Prefer using the context-based
 * approach (NodeSelectionPresenceProvider + useIsNodeSelectedByOthers) to avoid
 * duplicate subscriptions when used in multiple components.
 */
export function useNodeSelectionPresence(): Map<string, NodeSelectionPresence[]> {
  const inChatRoom = useInChatRoom();
  const {
    user
  } = useAuth();
  const myB3GlobalId = user?.userId;

  // Only use presence listener if we're in a chat room
  const {
    presenceData
  } = usePresenceListener({
    listener: () => {
      // We rely on presenceData state, no need for listener action
    }
  });

  // Track previous fingerprint and selections to avoid unnecessary re-renders
  const prevFingerprintRef = useRef<string>("");
  const prevSelectionsRef = useRef<Map<string, NodeSelectionPresence[]>>(new Map());
  const nodeSelections = useMemo(() => {
    if (!inChatRoom) {
      // Return empty map, but keep stable reference if already empty
      if (prevSelectionsRef.current.size === 0) {
        return prevSelectionsRef.current;
      }
      prevFingerprintRef.current = "";
      prevSelectionsRef.current = new Map();
      return prevSelectionsRef.current;
    }

    // Create fingerprint to check if selections actually changed
    const fingerprint = createSelectionFingerprint(presenceData, myB3GlobalId);

    // If fingerprint hasn't changed, return the same Map reference
    if (fingerprint === prevFingerprintRef.current) {
      return prevSelectionsRef.current;
    }

    // Selections changed - compute new map
    const selections = new Map<string, NodeSelectionPresence[]>();
    presenceData.forEach(member => {
      const selectedNodeId = member.data?.selectedNodeId;
      if (!selectedNodeId) return;

      // Skip our own selection (Ably clientId is the b3GlobalId)
      const isOwnSelection = myB3GlobalId && member.clientId === myB3GlobalId;
      if (isOwnSelection) return;
      const presence: NodeSelectionPresence = {
        nodeId: selectedNodeId,
        clientId: member.clientId,
        name: member.data?.name || member.clientId,
        color: generateColorFromAddress(member.clientId),
        b3GlobalId: member.clientId
      };
      const existing = selections.get(selectedNodeId) || [];
      existing.push(presence);
      selections.set(selectedNodeId, existing);
    });

    // Update refs for next comparison
    prevFingerprintRef.current = fingerprint;
    prevSelectionsRef.current = selections;
    return selections;
  }, [presenceData, myB3GlobalId, inChatRoom]);
  return nodeSelections;
}

/**
 * Hook to check if a specific node is selected by another user.
 * Reads from the NodeSelectionPresenceContext (requires NodeSelectionPresenceProvider ancestor).
 * Falls back to empty array if no provider is present.
 *
 * PERFORMANCE: Does NOT call usePresenceListener — reads from shared context.
 */
export function useIsNodeSelectedByOthers(nodeId) {
  const $ = _c(3);
  const nodeSelections = useContext(NodeSelectionPresenceContext);
  let t0;
  bb0: {
    if (!nodeId) {
      t0 = EMPTY_ARRAY;
      break bb0;
    }
    let t1;
    if ($[0] !== nodeId || $[1] !== nodeSelections) {
      t1 = nodeSelections.get(nodeId) || EMPTY_ARRAY;
      $[0] = nodeId;
      $[1] = nodeSelections;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t0 = t1;
  }
  return t0;
}